import type {Module} from "vuex";
import type {Grouping} from "@/global/types";
import type {RootState} from "@/store/types";
import {platform} from "@/api/platform";
import {retryRequest} from "@/helpers/request";
import {getBranchById, getParentsChain} from "@/helpers/tree";
import {CATALOG_ROOT_CATEGORY} from "@/constants";

export interface GroupingState {
    loading: boolean
    items: Grouping[]
    activeId: Grouping["id"] | null
    type: string | null
    rootItem: Grouping | null
}

export interface GroupingFetchPayload {
    type: string
    activeId?: Grouping["id"] | null
}

export interface GroupingFetchResult {
    activeId: Grouping["id"] | null
    items: Grouping[]
}

const grouping: Module<GroupingState, RootState> = {
    namespaced: true,
    state: {
        loading: false,
        items: [],
        activeId: null,
        type: null,
        rootItem: null
    },
    getters: {
        loading: (state): boolean => state.loading,
        items: (state): Grouping[] => state.items,
        find: state => (id: Grouping["id"]): Grouping | undefined => getBranchById(state.items, id) || state.items[0],
        activeId: (state): Grouping["id"] | null => state.activeId,
        active: (state): Grouping | null => getBranchById(state.items, state.activeId as Grouping["id"]),
        type: (state): string | null => state.type,
        chain: (state): Grouping[] => !state.activeId
        || (state.rootItem && state.rootItem.id == state.activeId)
        || !getBranchById(state.items, state.activeId)
            ? []
            : getParentsChain(state.items, getBranchById(state.items, state.activeId) as Grouping),
        rootItem: (state): Grouping | null => state.rootItem,
    },
    mutations: {
        setLoading: (state, loading: boolean) => state.loading = loading,
        setItems: (state, tree: Grouping[]) => state.items = tree,
        setActiveId: (state, activeId: Grouping["id"] | null) => state.activeId = activeId,
        setType: (state, type: string | null) => state.type = type,
        setRootItem: (state, rootItem: Grouping | null) => state.rootItem = rootItem
    },
    actions: {
        fetch(context, {type, activeId}: GroupingFetchPayload): Promise<GroupingFetchResult> {

            context.commit("setLoading", true);
            context.commit("setType", type);

            return retryRequest(() => platform.get<{results: {groupings: Grouping[]}}>(`/api/v2/groupingList/${type}`))
                .then(response => {

                    if (true === CATALOG_ROOT_CATEGORY) {

                        context.commit("setRootItem", response.data.results.groupings[0]);
                        context.commit("setItems", response.data.results.groupings[0].children);
                    } else {

                        context.commit("setItems", response.data.results.groupings);
                    }

                    if (activeId) {

                        context.commit("setActiveId", activeId);
                    }

                    return {
                        activeId: context.getters["activeId"],
                        items: context.getters["items"]
                    }
                })
                .finally(() => context.commit("setLoading", false))
        },
        setActiveId: (context, activeId: Grouping["id"] | null) => context.commit("setActiveId", activeId)
    }
}

export default grouping
